/* ═══════════════════════════════════════════════════════════════════════════
   Roles — canonical model + capabilities

   KAPinsights moved from 7 roles to 5:
     Owner ⊇ Manager ⊇ Analyst ⊇ Contributor ⊇ Viewer   (higher inherits lower)

   The backend may still return the OLD role names for a while, so everything
   goes through normalizeRole() first — the rest of the app should reason about
   the 5 canonical roles and the capability helpers below, never raw strings.

   Old → new mapping:
     Admin                              → owner
     Project Manager                    → manager
     Technical Expert + Quality Assurance → analyst
     Data Collector + Translation People  → contributor
     Guest                              → viewer
   ═══════════════════════════════════════════════════════════════════════════ */

export type CanonicalRole =
  | "owner"
  | "manager"
  | "analyst"
  | "contributor"
  | "viewer";

/** Any known name (old or new, any casing) → canonical role. */
const ROLE_ALIASES: Record<string, CanonicalRole> = {
  // new names
  owner: "owner",
  manager: "manager",
  analyst: "analyst",
  contributor: "contributor",
  viewer: "viewer",
  // old names (still returned by the backend during the transition)
  admin: "owner",
  "project manager": "manager",
  "technical expert": "analyst",
  "quality assurance": "analyst",
  qa: "analyst",
  "data collector": "contributor",
  "translation people": "contributor",
  translator: "contributor",
  guest: "viewer",
};

/** Rank for the capability hierarchy — higher can do everything lower can. */
const RANK: Record<CanonicalRole, number> = {
  owner: 5,
  manager: 4,
  analyst: 3,
  contributor: 2,
  viewer: 1,
};

/** Human-readable label for a canonical role. */
export const ROLE_LABEL: Record<CanonicalRole, string> = {
  owner: "Owner",
  manager: "Manager",
  analyst: "Analyst",
  contributor: "Contributor",
  viewer: "Viewer",
};

/** Normalize any role name (old or new, any casing) to a canonical role. */
export const normalizeRole = (raw?: string | null): CanonicalRole | null => {
  if (!raw) return null;
  return ROLE_ALIASES[raw.trim().toLowerCase()] ?? null;
};

/** true if `role` ranks at or above `min` in the hierarchy. */
export const atLeast = (
  role: CanonicalRole | null,
  min: CanonicalRole,
): boolean => role != null && RANK[role] >= RANK[min];

/** Current signed-in user's canonical role (from localStorage), or null. */
export const getCurrentRole = (): CanonicalRole | null => {
  const userData = localStorage.getItem("user");
  if (!userData) return null;
  try {
    const user = JSON.parse(userData);
    const raw =
      user.roles && user.roles.length > 0 ? user.roles[0].role_name : null;
    return normalizeRole(raw);
  } catch {
    return null;
  }
};

// ── Capabilities ─────────────────────────────────────────────────────────────
// Derived from the permission matrix. Prefer these over comparing role strings.

/** Billing & subscription — Owner only. */
export const canManageBilling = (r: CanonicalRole | null): boolean =>
  r === "owner";

/** Create/edit/delete workspaces — Owner only. */
export const canManageWorkspaces = (r: CanonicalRole | null): boolean =>
  r === "owner";

/** Invite/manage members — Owner (all) or Manager (within workspace). */
export const canManageUsers = (r: CanonicalRole | null): boolean =>
  r === "owner" || r === "manager";

/**
 * Create a project. Owner is a superset of Manager, so a solo Owner can create
 * (and be the project's manager) without inviting anyone.
 */
export const canCreateProject = (r: CanonicalRole | null): boolean =>
  r === "owner" || r === "manager";

/** Permanently delete projects/workspaces — Owner + Manager only. */
export const canDeleteProject = (r: CanonicalRole | null): boolean =>
  r === "owner" || r === "manager";

/** Build & publish analysis (dashboards, scorecards, formulas) — Analyst and up. */
export const canBuildAnalyze = (r: CanonicalRole | null): boolean =>
  atLeast(r, "analyst");

/** Upload/manage raw data, documents, translations — Contributor and up. */
export const canUploadData = (r: CanonicalRole | null): boolean =>
  atLeast(r, "contributor");

/** View original (pre-anonymised) files — Owner only. */
export const canViewOriginals = (r: CanonicalRole | null): boolean =>
  r === "owner";

// ── Seat classification (for subscription billing / caps) ─────────────────────

/** Paid "working" seat — counts toward the plan's working-user cap. */
export const isWorkingSeat = (r: CanonicalRole | null): boolean =>
  atLeast(r, "analyst"); // owner, manager, analyst

/** Free, uncapped seat — never billed, never counted. */
export const isFreeSeat = (r: CanonicalRole | null): boolean =>
  r === "contributor" || r === "viewer";

/** The roles that consume a paid working seat. */
export const WORKING_ROLES: CanonicalRole[] = ["owner", "manager", "analyst"];

/** The roles that are always free / uncapped. */
export const FREE_ROLES: CanonicalRole[] = ["contributor", "viewer"];
